/* eslint-disable no-unused-vars */
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Button from 'Components/UI/Button';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { darken } from '@material-ui/core/styles/colorManipulator';
import { makeStyles } from '@material-ui/core/styles';
import Folder from 'Classes/Folder';
import FolderModal from '../FolderModal';

const ROOT_FOLDER_NAME = 'Home';

const useStyles = makeStyles(theme => ({
  fileStoreNavigator: {
    padding: theme.spacing(2.25),
    minWidth: '300px',
  },
  currentFolder: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  folder: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  folderName: {
    marginLeft: theme.spacing(0.625),
  },
  moveButton: {
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'block',
    marginTop: theme.spacing(1.25),
    backgroundColor: theme.palette.facet.primaryBlue,
    color: theme.palette.common.white,
    borderRadius: theme.radius.none,
    '&:hover': {
      backgroundColor: darken(
        theme.palette.facet.primaryBlue,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  grey: {
    color: theme.palette.facet.primaryGray,
  },
  primaryBlue: {
    color: theme.palette.facet.primaryBlue,
  },
  currentFolderName: {
    color: theme.palette.facet.primaryBlue,
    marginLeft: theme.spacing(0.625),
  },
  undoMove: {
    padding: theme.spacing(2.25),
  },
  undoLocation: {
    marginTop: theme.spacing(0.625),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& i': {
      marginRight: theme.spacing(0.625),
    },
  },
  undoButton: {
    marginLeft: theme.spacing(0.625),
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  newFolder: {
    paddingTop: theme.spacing(0.625),
    marginTop: theme.spacing(0.625),
    borderTop: `1px solid ${theme.palette.facet.lightGray}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontWeight: theme.fontWeightMedium,
    color: theme.palette.facet.accentBlue,
    '& i': {
      color: theme.palette.facet.accentGreen,
      marginRight: theme.spacing(0.625),
    },
  },
}));

function FilestoreNavigatorPopover({
  anchorEl,
  moveFile,
  file,
  undoFileMove,
  onClose,
  createFolder,
}) {
  const { getFilestoreResource } = useSwaggerlessContext();
  const classes = useStyles();
  const { household } = useSimpleHousehold();
  const [currentFolder, setCurrentFolder] = useState(new Folder());
  const [currentFolderName, setCurrentFolderName] = useState(ROOT_FOLDER_NAME);
  const [visitedFolders, setVisitedFolders] = useState([]);
  const [previouslyVisitedName, setPreviouslyVisitedName] = useState('');
  const [lastMovedFile, setLastMovedFile] = useState(null);
  const [coordinates, setCoordinates] = useState({ left: 0, top: 0 });
  const [isNewFolderModalOpen, setNewFolderModalOpen] = useState(false);

  /* anchorEl is the element passed to this component to anchor the popover to
     Ideally we could just pass that to the popover Component but we have to do a little additional work
     Why?
     When you move a file the anchor element disappears.  Here we'll save the coordinates so that we can always
     anchor to the location of the file for the undo popover
  */
  useEffect(() => {
    setCoordinates({
      left: anchorEl ? anchorEl.getBoundingClientRect().x : 0,
      top: anchorEl ? anchorEl.getBoundingClientRect().y : 0,
    });
    setLastMovedFile(null);
  }, [anchorEl]);

  /* We have to save the folder name in addition to the folder in the state
     This is because the getFilestoreResource endpoint returns only the contents of a folder
     and not the name of the folder.
     We have access to the folder name becuase it was a child of the previous folder fetch.  The children
     of a folder, do have their names in the response
  */
  function updateCurrentFolder(folderId, folderName) {
    return getFilestoreResource(household.id, folderId).then((res) => {
      setCurrentFolder(Folder.fromAPIResponse(res, folderId));
      setCurrentFolderName(folderName);
      setPreviouslyVisitedName(currentFolderName);
      setVisitedFolders([...visitedFolders, currentFolder]);
    });
  }

  function handleFolderClick(selectedFolder) {
    updateCurrentFolder(selectedFolder.id, selectedFolder.attributes.name);
  }

  function handleFolderBackClick() {
    setCurrentFolder(visitedFolders[visitedFolders.length - 1]);
    setCurrentFolderName(previouslyVisitedName);
    setVisitedFolders(visitedFolders.slice(0, -1));
  }

  function handleClose() {
    onClose();
    setLastMovedFile(null);
    updateCurrentFolder(household.folderId, ROOT_FOLDER_NAME);
  }

  useEffect(() => {
    if (household.folderId > 0) {
      updateCurrentFolder(household.folderId, ROOT_FOLDER_NAME);
    }
  }, [household.folderId]);

  const childFolders = currentFolder.children.filter(
    child => child.type === 'folder',
  );

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorReference="anchorPosition"
        anchorPosition={coordinates}
        onClose={handleClose}
        disableEnforceFocus
        transitionDuration={0}
      >
        {lastMovedFile ? (
          <div className={classes.undoMove}>
            <Typography className={classes.primaryBlue} variant="h6">
              File moved to:
            </Typography>
            <div className={classes.undoLocation}>
              <FontAwesome
                className={classes.primaryBlue}
                icon="folder-o"
                fontSize="16px"
              />
              <Typography className={classes.primaryBlue} variant="body1">
                {currentFolderName}
              </Typography>
              <Typography
                className={`${classes.undoButton} ${classes.grey}`}
                variant="body1"
                onClick={() => {
                  handleClose();
                  undoFileMove(lastMovedFile);
                }}
              >
                Undo
              </Typography>
            </div>
          </div>
        ) : (
          <>
            <div className={classes.fileStoreNavigator}>
              {currentFolderName !== ROOT_FOLDER_NAME && (
                <div
                  className={classes.currentFolder}
                  onClick={handleFolderBackClick}
                  role="presentation"
                >
                  <FontAwesome icon="caret-left" className={classes.grey} />
                  <Typography
                    className={classes.currentFolderName}
                    variant="body1"
                  >
                    {currentFolderName}
                  </Typography>
                </div>
              )}
              <div className={classes.currentFolderChildren}>
                {childFolders.map((f, i) => (
                  <div
                    key={`child-folders.${i}`} // eslint-disable-line react/no-array-index-key
                    className={`${classes.folder} ${classes.grey}`}
                    onClick={() => {
                      handleFolderClick(f);
                    }}
                    role="presentation"
                  >
                    <FontAwesome icon="folder-o" fontSize="16px" />
                    <Typography
                      className={`${classes.folderName} ${classes.grey}`}
                      variant="h6"
                    >
                      {f.attributes.name}
                    </Typography>
                  </div>
                ))}
              </div>
              <div
                className={classes.newFolder}
                onClick={() => {
                  setNewFolderModalOpen(true);
                }}
                role="presentation"
              >
                <FontAwesome
                  icon="plus-circle"
                  test="New Folder"
                  fontSize="12px"
                />
                <Typography variant="body1">New Folder</Typography>
              </div>
              <Button
                variant="contained"
                className={classes.moveButton}
                disableElevation
                onClick={() => {
                  setLastMovedFile(file);
                  moveFile(file.id, currentFolder.id);
                }}
              >
                <Typography variant="body1">Move here</Typography>
              </Button>
            </div>
          </>
        )}
      </Popover>
      <FolderModal
        isOpen={isNewFolderModalOpen}
        closeModal={() => {
          setNewFolderModalOpen(false);
        }}
        createFolder={reqBody => createFolder(reqBody)}
        folderID={currentFolder.id}
        folderName={currentFolder?.attributes?.name}
        refreshFolder={(folderId, onSuccess, onFail) => {
          updateCurrentFolder(currentFolder.id, currentFolderName);
          onSuccess();
        }}
        householdId={household.id}
        zIndex={2000}
      />
    </>
  );
}

FilestoreNavigatorPopover.propTypes = {
  anchorEl: PropTypes.object,
  file: PropTypes.object.isRequired,
  moveFile: PropTypes.func.isRequired,
  undoFileMove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  createFolder: PropTypes.func.isRequired,
};

FilestoreNavigatorPopover.defaultProps = {
  anchorEl: null,
};

export default FilestoreNavigatorPopover;
